/**
 * colormath.ts — Pure color mathematics used across all engine modules.
 *
 * No imports from other engine files. No side effects.
 */

import type { DeviceAnchors, Lab, RGB } from '@/types';

// =============================================================================
// Basic Lab utilities
// =============================================================================

/** Euclidean chroma: C* = sqrt(a*² + b*²) */
export function chroma([, a, b]: Lab): number {
  return Math.sqrt(a * a + b * b);
}

/** Hue angle in degrees [0, 360) */
export function hueAngle([, a, b]: Lab): number {
  const h = Math.atan2(b, a) * (180 / Math.PI);
  return h < 0 ? h + 360 : h;
}

/** Shortest hue arc between two hue angles, always in [0, 180] */
export function hueDiff(h1: number, h2: number): number {
  const d = Math.abs(h1 - h2) % 360;
  return d > 180 ? 360 - d : d;
}

/** Mean of an array of numbers */
export function mean(vals: number[]): number {
  if (vals.length === 0) return 0;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

/** Standard deviation (population) */
export function stdDev(vals: number[]): number {
  if (vals.length < 2) return 0;
  const m = mean(vals);
  return Math.sqrt(mean(vals.map(v => (v - m) * (v - m))));
}

// =============================================================================
// ΔE formulas
// =============================================================================

/** ΔE76 — Euclidean distance in Lab space */
export function deltaE76(a: Lab, b: Lab): number {
  return Math.sqrt(
    (a[0] - b[0]) * (a[0] - b[0]) +
    (a[1] - b[1]) * (a[1] - b[1]) +
    (a[2] - b[2]) * (a[2] - b[2]),
  );
}

/**
 * ΔE2000 — full CIE implementation.
 * Uses kL=kC=kH=1 (standard parametric factors).
 * Falls back to ΔE76 for near-neutral pairs (both C* < 0.5) for performance.
 */
export function deltaE2000(lab1: Lab, lab2: Lab): number {
  const [L1, a1, b1] = lab1;
  const [L2, a2, b2] = lab2;

  const sq = (x: number) => x * x;
  const pow7 = (x: number) => x * x * x * x * x * x * x;

  const C1ab = Math.sqrt(sq(a1) + sq(b1));
  const C2ab = Math.sqrt(sq(a2) + sq(b2));

  // Near-neutral shortcut: ΔE76 ≈ ΔE2000 when both chromas are tiny
  if (C1ab < 0.5 && C2ab < 0.5) return deltaE76(lab1, lab2);

  const Cbar  = (C1ab + C2ab) / 2;
  const Cbar7 = pow7(Cbar);
  const G     = 0.5 * (1 - Math.sqrt(Cbar7 / (Cbar7 + pow7(25))));

  const a1p = a1 * (1 + G);
  const a2p = a2 * (1 + G);
  const C1p = Math.sqrt(sq(a1p) + sq(b1));
  const C2p = Math.sqrt(sq(a2p) + sq(b2));

  const h1p = C1p < 1e-10 ? 0 : (Math.atan2(b1, a1p) * 180 / Math.PI + 360) % 360;
  const h2p = C2p < 1e-10 ? 0 : (Math.atan2(b2, a2p) * 180 / Math.PI + 360) % 360;

  const dLp = L2 - L1;
  const dCp = C2p - C1p;

  let dhp: number;
  if (C1p * C2p < 1e-10) {
    dhp = 0;
  } else if (Math.abs(h2p - h1p) <= 180) {
    dhp = h2p - h1p;
  } else if (h2p - h1p > 180) {
    dhp = h2p - h1p - 360;
  } else {
    dhp = h2p - h1p + 360;
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp / 2) * Math.PI / 180);

  const Lbar  = (L1 + L2) / 2;
  const Cbarp = (C1p + C2p) / 2;

  let Hbarp: number;
  if (C1p * C2p < 1e-10) {
    Hbarp = h1p + h2p;
  } else if (Math.abs(h1p - h2p) <= 180) {
    Hbarp = (h1p + h2p) / 2;
  } else if (h1p + h2p < 360) {
    Hbarp = (h1p + h2p + 360) / 2;
  } else {
    Hbarp = (h1p + h2p - 360) / 2;
  }

  const deg = (x: number) => x * Math.PI / 180;
  const T = 1
    - 0.17 * Math.cos(deg(Hbarp - 30))
    + 0.24 * Math.cos(deg(2 * Hbarp))
    + 0.32 * Math.cos(deg(3 * Hbarp + 6))
    - 0.20 * Math.cos(deg(4 * Hbarp - 63));

  const SL = 1 + 0.015 * sq(Lbar - 50) / Math.sqrt(20 + sq(Lbar - 50));
  const SC = 1 + 0.045 * Cbarp;
  const SH = 1 + 0.015 * Cbarp * T;

  const Cbarp7 = pow7(Cbarp);
  const RC     = 2 * Math.sqrt(Cbarp7 / (Cbarp7 + pow7(25)));
  const dTheta = 30 * Math.exp(-sq((Hbarp - 275) / 25));
  const RT     = -Math.sin(deg(2 * dTheta)) * RC;

  return Math.sqrt(
    sq(dLp / SL) +
    sq(dCp / SC) +
    sq(dHp / SH) +
    RT * (dCp / SC) * (dHp / SH),
  );
}

// =============================================================================
// Ink / virtual CMY conversions
// =============================================================================

/** RGB channel value → ink level [0=no ink, 1=full ink] */
export const rgbToInk = (v: number): number => 1 - v / 255;

/** Ink level → RGB channel value (0–255) */
export const inkToRgb = (ink: number): number => Math.round((1 - ink) * 255);

/** Sum of all three ink levels (0–3) */
export const totalInk = ([r, g, b]: RGB): number =>
  rgbToInk(r) + rgbToInk(g) + rgbToInk(b);

// =============================================================================
// Tone target (NPDC-inspired linear perceptual response)
// =============================================================================

/**
 * Target L* for a neutral patch at RGB channel value v (0–255).
 * Scaled between the actual measured device endpoints (anchors.black.L … anchors.white.L)
 * rather than the theoretical [0, 100].
 * Slight toe below RGB≈20 to smooth shadow clipping.
 */
export function targetLstar(v: number, anchors: DeviceAnchors): number {
  const t     = v / 255;
  const range = anchors.white.L - anchors.black.L;
  if (t > 0.08) return anchors.black.L + t * range;
  return anchors.black.L + Math.pow(t / 0.08, 1.2) * range * 0.08;
}

// =============================================================================
// Monotonicity check
// =============================================================================

/**
 * Returns indices where values[i] < values[i-1] - tolerance
 * (i.e., where the sequence unexpectedly decreases when it should be non-decreasing,
 * or unexpectedly increases when flagged for non-increasing).
 */
export function monotonicViolations(
  values: number[],
  direction: 'increasing' | 'decreasing' = 'increasing',
  tolerance = 1e-6,
): number[] {
  const violations: number[] = [];
  for (let i = 1; i < values.length; i++) {
    const prev = values[i - 1]!;
    const curr = values[i]!;
    if (direction === 'increasing'  && curr < prev - tolerance) violations.push(i);
    if (direction === 'decreasing'  && curr > prev + tolerance) violations.push(i);
  }
  return violations;
}

// =============================================================================
// Channel index for each patch group
// =============================================================================

/**
 * Which RGB array index drives ink for a given group.
 *   R  → index 0 (R channel decreases as ink increases, G=B=255)
 *   G  → index 1
 *   B  → index 2
 *   RG → index 0 (R and G both decrease equally)
 *   RB → index 0
 *   GB → index 1
 *   N  → index 0 (R=G=B decreases equally)
 *   NN → index 0 (neutral axis)
 */
export const CHANNEL_IDX: Record<string, number> = {
  R: 0, RG: 0, RB: 0,
  G: 1, GB: 1,
  B: 2,
  N: 0,
  NN32: 0, NN64: 0, NN96: 0, NN128: 0, NN160: 0, NN192: 0, NN224: 0,
};

/**
 * Sort patches by ink level ascending (lowest ink = highest RGB = white first).
 * Used by diagnostics and cleaning.
 */
export function sortByInk<T extends { rgb: RGB; group: string }>(patches: T[]): T[] {
  const group = patches[0]?.group ?? 'R';
  const ch    = CHANNEL_IDX[group] ?? 0;
  // Higher RGB = lower ink → sort descending by RGB to get ascending ink order
  return [...patches].sort((a, b) => b.rgb[ch as 0|1|2] - a.rgb[ch as 0|1|2]);
}

// =============================================================================
// Simple percentile (linear interpolation)
// =============================================================================

export function percentile(arr: number[], p: number): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx    = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx), hi = Math.ceil(idx);
  return (sorted[lo] ?? 0) + ((sorted[hi] ?? 0) - (sorted[lo] ?? 0)) * (idx - lo);
}

// =============================================================================
// Spectral → CIELab (D50 / 2°)
// =============================================================================

// CIE 1931 2° CMFs × D50 SPD, 380–730 nm at 10 nm steps (36 wavelengths).
// Products are pre-multiplied (CMF[i] * D50[i]) so the inner loop is minimal.
// Source: CIE Publication 15:2004 Table 1 + CIE 015:2018 Table 1.
const _SPD_XD50 = [
  0.032766, 0.108044, 0.386031, 1.714735, 6.041330, 13.276476,
  16.898038, 17.761040, 16.641720, 11.871427, 5.902197, 2.003122,
  0.326949, 0.634236, 4.403601, 12.440309, 23.462539, 35.567928,
  49.592720, 62.823454, 74.764570, 84.636636, 88.489782, 84.419220,
  72.690180, 54.355240, 37.802820, 23.834250, 13.802230, 7.288540,
  3.882958, 1.890664, 0.948408, 0.484453, 0.243672, 0.121278,
];
const _SPD_YD50 = [
  0.000934, 0.003054, 0.010683, 0.047691, 0.179920, 0.542864,
  1.115900, 2.006800, 3.438000, 5.527544, 8.580948, 13.005810,
  21.548180, 34.284400, 49.381700, 64.798940, 77.085420, 81.560903,
  82.912700, 78.478880, 70.960740, 62.390740, 52.517810, 42.302600,
  32.391900, 22.434050, 14.774500, 9.004850, 5.106020, 2.668480,
  1.412220, 0.683562, 0.342603, 0.174921, 0.087956, 0.043795,
];
const _SPD_ZD50 = [
  0.154419, 0.510773, 1.830254, 8.173960, 29.050560, 64.838304,
  84.804013, 93.593741, 95.602440, 78.283742, 50.186065, 29.107560,
  18.150520, 10.786760, 5.443785, 3.168502, 1.639289, 0.717688,
  0.324918, 0.173061, 0.134554, 0.090684, 0.066641, 0.028618,
  0.016149, 0.004236, 0.001688, 0.000000, 0.000000, 0.000000,
  0.000000, 0.000000, 0.000000, 0.000000, 0.000000, 0.000000,
];

// D50 white-point (perfect diffuser reflectance = 1 at all wavelengths)
const _YN = _SPD_YD50.reduce((s, v) => s + v, 0);
const _XN = _SPD_XD50.reduce((s, v) => s + v, 0);
const _ZN = _SPD_ZD50.reduce((s, v) => s + v, 0);

function _fLab(t: number): number {
  const d = 6 / 29;
  return t > d * d * d ? Math.cbrt(t) : t / (3 * d * d) + 4 / 29;
}

/**
 * Convert a spectral reflectance curve to CIELab (D50/2°).
 * @param nm     Wavelengths in nm. Any subset of 380–730 nm accepted; out-of-range ignored.
 * @param values Reflectance values (0–1 scale) corresponding to each wavelength.
 */
export function spectralToLab(nm: number[], values: number[]): Lab {
  let X = 0, Y = 0, Z = 0;
  for (let k = 0; k < nm.length; k++) {
    const wl = nm[k]!;
    const i  = Math.round((wl - 380) / 10);
    if (i < 0 || i >= 36) continue;
    const r = values[k]!;
    X += _SPD_XD50[i]! * r;
    Y += _SPD_YD50[i]! * r;
    Z += _SPD_ZD50[i]! * r;
  }
  const xr = X / _XN, yr = Y / _YN, zr = Z / _ZN;
  const L = 116 * _fLab(yr) - 16;
  const a = 500 * (_fLab(xr) - _fLab(yr));
  const b = 200 * (_fLab(yr) - _fLab(zr));
  return [L, a, b];
}

/**
 * Convert absolute XYZ (D50) to CIELab.
 * Input scale: Y = 100 for a perfect diffuser under D50 (standard CGATS convention).
 */
export function xyzToLab([x, y, z]: [number, number, number]): Lab {
  // D50 white point XYZ (Y=100 scale): X=96.422, Y=100, Z=82.521
  const L = 116 * _fLab(y / 100.0) - 16;
  const a = 500 * (_fLab(x / 96.422) - _fLab(y / 100.0));
  const b = 200 * (_fLab(y / 100.0) - _fLab(z / 82.521));
  return [L, a, b];
}
